import React from 'react';
import PropTypes from 'prop-types';
import {
  Card, Tag, Tooltip, Typography,
} from 'antd';
import ShopifyProductLinker from 'components/ShopifyProductLinker/ShopifyProductLinker';
import SkuCin7InlineEditor from 'components/SkuCin7InlineEditor/SkuCin7InlineEditor';
import KountaProductLinker from 'components/KountaProductLinker/KountaProductLinker';
import {
  mondaySkuPulseHref,
  SHOPIFY_COMPARED_STORE,
  SHOPIFY_READONLY_STORE,
  skuPlatformLinkMissing,
  stopRowClick,
} from './helpers';
import './SkuLinksPanel.less';

const { Text } = Typography;

const MONDAY_READONLY = 'Monday item id is set in Product Syncing and is not edited here.';

function LinkCard({ name, missing, children }) {
  return (
    <Card
      size="small"
      className={missing ? 'sku-link-card--missing' : undefined}
      bodyStyle={{ padding: 12 }}
    >
      <Text strong>{name}</Text>
      <div style={{ marginTop: 8 }}>{children}</div>
    </Card>
  );
}

LinkCard.propTypes = {
  name: PropTypes.string.isRequired,
  missing: PropTypes.bool,
  children: PropTypes.node,
};

LinkCard.defaultProps = {
  missing: false,
  children: null,
};

/**
 * Editable platform links for one SKU, using the Product Database linkers.
 * Monday stays read-only: that link is owned by Product Syncing.
 */
export default function SkuLinksPanel({
  sku, beerName, disabled, sharedCin7, sharedKounta, onChanged, onKountaCatalogSkuChange,
  highlightMissingLinks,
}) {
  const productId = sku?.productMatrixId;
  const mondayHref = mondaySkuPulseHref(sku?.mondayId);
  const matcherSku = {
    ...sku,
    name: sku?.skuName,
    cin7Sku: sku?.cin7Sku,
  };
  const missing = highlightMissingLinks ? skuPlatformLinkMissing(sku) : null;

  return (
    <div>
      <Text strong style={{ display: 'block', marginBottom: 8 }}>Platform links</Text>
      <div className="sku-link-cards">
        <LinkCard name="Monday" missing={Boolean(missing?.monday)}>
          {mondayHref ? (
            <a href={mondayHref} target="_blank" rel="noopener noreferrer" onClick={stopRowClick}>
              {sku.mondayId}
            </a>
          ) : (
            <Text type="secondary">Not linked</Text>
          )}
          <div style={{ marginTop: 6 }}>
            <Tooltip title={MONDAY_READONLY}>
              <Tag style={{ marginRight: 0 }}>Read-only</Tag>
            </Tooltip>
          </div>
        </LinkCard>
        <LinkCard name="Shopify NZ" missing={Boolean(missing?.shopifyNz)}>
          <ShopifyProductLinker
            skuKey={sku.skuKey}
            productMatrixId={productId}
            store={SHOPIFY_COMPARED_STORE}
            disabled={disabled}
            onSaved={onChanged}
          />
        </LinkCard>
        <LinkCard name="Shopify AU" missing={Boolean(missing?.shopifyAu)}>
          <ShopifyProductLinker
            skuKey={sku.skuKey}
            productMatrixId={productId}
            store={SHOPIFY_READONLY_STORE}
            disabled={disabled}
            onSaved={onChanged}
          />
        </LinkCard>
        <LinkCard name="Cin7" missing={Boolean(missing?.cin7)}>
          <SkuCin7InlineEditor
            productId={productId}
            skuKey={sku.skuKey}
            sku={matcherSku}
            parentProductName={beerName}
            compact
            fullWidth
            searchOnly
            sharedCachedProducts={sharedCin7}
            onSaved={onChanged}
          />
        </LinkCard>
        <LinkCard name="Kounta" missing={Boolean(missing?.kounta)}>
          <KountaProductLinker
            skuKey={sku.skuKey}
            sharedKountaCatalog={sharedKounta}
            onLinksChanged={onChanged}
            onCatalogSkuChange={onKountaCatalogSkuChange}
          />
        </LinkCard>
      </div>
    </div>
  );
}

SkuLinksPanel.propTypes = {
  sku: PropTypes.shape({
    skuKey: PropTypes.number,
    skuName: PropTypes.string,
    productMatrixId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    mondayId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    cin7Sku: PropTypes.string,
    containerType: PropTypes.string,
    unitType: PropTypes.string,
    packType: PropTypes.string,
    caseType: PropTypes.string,
  }).isRequired,
  beerName: PropTypes.string,
  disabled: PropTypes.bool,
  sharedCin7: PropTypes.arrayOf(PropTypes.shape({})),
  sharedKounta: PropTypes.arrayOf(PropTypes.shape({})),
  onChanged: PropTypes.func,
  onKountaCatalogSkuChange: PropTypes.func,
  highlightMissingLinks: PropTypes.bool,
};

SkuLinksPanel.defaultProps = {
  beerName: '',
  disabled: false,
  sharedCin7: undefined,
  sharedKounta: undefined,
  onChanged: undefined,
  onKountaCatalogSkuChange: undefined,
  highlightMissingLinks: false,
};
